import React, { CSSProperties, useCallback } from 'react';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { useIntl } from 'react-intl';
import { CommonTranslationKeys } from 'intl/keys';
import { ButtonContained, ButtonText } from '../../../general';
import { styles } from './dialogStyles';
import useIsMobile from 'hooks/useIsMobile';
import {
  Dialog as MaterialDialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
} from '@mui/material';
import { Color } from 'shared/utils/colors';

export enum DialogType {
  info = 'info',
}

export interface DialogOptions {
  title?: string | React.ReactNode;
  controllersOff?: boolean;
  description?: string | React.ReactNode;
  catchOnCancel?: boolean;
  customControllerLabels?: (string | null)[];
  fullWidth?: boolean;
  controllerStyles?: CSSProperties;
  submitContained?: boolean;
  type?: string;
  onDialogSubmit?: () => void;
  onDialogClose?: () => void;
  submitLabel?: string;
  rejectOnOffline?: boolean;
  maxWidth?: number;
  isDangerousAction?: boolean;
}

interface DialogProps extends DialogOptions {
  open: boolean;
  onSubmit?: () => void;
  onClose?: () => void;
  submitDisabled?: boolean;
  cancelLabel?: string;
}

const dialogProps = (isMobile: boolean, maxWidth?: number): object => ({
  style: {
    zIndex: 40000,
  },
  PaperProps: {
    style: {
      minWidth: isMobile ? 'min(95%, 300px)' : 500,
      maxWidth: maxWidth || 800,
    },
  },
});

const Dialog: React.FC<DialogProps> = ({
  open,
  title,
  type,
  description,
  fullWidth,
  customControllerLabels,
  controllerStyles,
  submitContained,
  onSubmit,
  onClose,
  onDialogSubmit,
  onDialogClose,
  submitDisabled,
  controllersOff,
  cancelLabel,
  submitLabel,
  maxWidth,
  isDangerousAction,
}) => {
  const isMobile = useIsMobile();
  const intl = useIntl();

  const resolveLabel = useCallback(
    (labelOrId: string): string =>
      formatTranslation(intl, unsafeTranslationKey(labelOrId)),
    [intl]
  );

  const CANCEL =
    customControllerLabels?.[0] != null
      ? resolveLabel(customControllerLabels[0])
      : resolveLabel(cancelLabel || CommonTranslationKeys.cancel);
  const SUBMIT =
    customControllerLabels?.[1] != null
      ? resolveLabel(customControllerLabels[1])
      : resolveLabel(submitLabel || CommonTranslationKeys.yes);

  const handleClose = useCallback((): void => {
    if (onDialogClose) {
      onDialogClose();
    }

    onClose?.();
  }, [onClose, onDialogClose]);

  const handleSubmit = (): void => {
    if (onDialogSubmit) {
      onDialogSubmit();
    }

    onSubmit?.();
  };

  const SubmitButton = submitContained ? ButtonContained : ButtonText;

  return (
    <MaterialDialog 
      open={open} 
      onClose={handleClose}
      {...dialogProps(isMobile, maxWidth)}
    >
      <div style={styles.dialogHeader}>
        {title && (
          <DialogTitle id='alert-dialog-title'>{title}</DialogTitle>
        )}
      </div>
      <Divider />
      <DialogContent
        style={{
          ...styles.dialogContent,
          ...(fullWidth && styles.fullWidth),
          ...(isDangerousAction && { backgroundColor: Color.redWithOpacity }),
        }}
      >
        {description}
      </DialogContent>
      {!controllersOff && (
        <DialogActions
          style={{ ...styles.dialogActions, ...controllerStyles }}
        >
          {type !== DialogType.info && (
            <ButtonText
              onClick={handleClose}
              data-qa='dialog-discard-button'
            >
              {CANCEL}
            </ButtonText>
          )}
          <ButtonContained
            onClick={handleSubmit}
            disabled={submitDisabled}
            data-qa='dialog-confirm-button'
            {...(isDangerousAction && {
              sx: { backgroundColor: Color.red, color: Color.white },
            })}
          >
            {SUBMIT}
          </ButtonContained>
        </DialogActions>
      )}
    </MaterialDialog>
  );
};

export default Dialog;
